import { lazy, ReactElement } from 'react';
import { Route } from 'react-router';

import { useConfig } from '#/hooks';
import { Path } from '#/service/routes';
import { useAuthentication } from '#/store/auth';

const PurchaseGift = lazy(
  () =>
    import(
      /* webpackChunkName: "PurchaseGift" */ '#/pages/Gifting/Purchase/PurchasePage'
    )
);

const RedeemGift = lazy(
  () =>
    import(
      /* webpackChunkName: "RedeemGift" */ '#/pages/Gifting/Redeem/RedeemPage'
    )
);

/**
 * Hook that dynamically returns a set of gifting routes. It's done as a hook to allow lazy loading
 * and to overcome React Router limitations for nesting routes (because they are not really nested
 * from React Router perspective, but we want them to behave like they are.
 * This way App component is not garbaged with this logic).
 */
export function useGiftingRoutes(): ReactElement[] {
  const { featureConfig } = useConfig();
  const { isLoggedIn } = useAuthentication();
  const { enabled = false, anonymousAllowed } =
    featureConfig.purchase.gifting ?? {};
  const purchaseEnabled = enabled && (isLoggedIn || anonymousAllowed);
  const redeemEnabled = enabled;

  return [
    redeemEnabled && <Route element={<RedeemGift />} path={Path.GiftRedeem} />,
    purchaseEnabled && (
      <Route element={<PurchaseGift />} path={Path.GiftPurchase} />
    ),
  ].filter(Boolean);
}
